import { useState, useEffect } from "react";
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  FileCheck,
  Lock,
  Download,
  Upload,
  RefreshCw,
  Settings,
  Info,
  FileText,
  Hash,
  Calendar,
  Building2,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import axios from "axios";

import API from "@/utils/api";

export default function AdminVeriFactu() {
  const [config, setConfig] = useState({
    enabled: false,
    nif: "",
    company_name: "",
    software_name: "Conexo POS POS",
    software_version: "2.0.0",
    manufacturer_nif: "",
    manufacturer_name: "",
    device_id: "",
    last_sync: null,
    total_invoices: 0,
    pending_signatures: 0,
    certificate_valid: false,
    certificate_expiry: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    fetchConfig();
    fetchRecentInvoices();
  }, []);

  const fetchConfig = async () => {
    try {
      setIsLoading(true);
      // Simulated - will be replaced with actual API call
      // const response = await axios.get(`${API}/admin/verifactu/config`, { withCredentials: true });
      // setConfig(response.data);

      // Temporary demo data
      setTimeout(() => {
        setConfig({
          enabled: false,
          nif: "B12345678",
          company_name: "Conexo POS S.L.",
          software_name: "Conexo POS POS",
          software_version: "2.0.0",
          manufacturer_nif: "B87654321",
          manufacturer_name: "Local Flow",
          device_id: "POS-001-Conexo POS",
          last_sync: null,
          total_invoices: 0,
          pending_signatures: 0,
          certificate_valid: false,
          certificate_expiry: null
        });
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error("Error fetching VeriFactu config:", error);
      toast.error("Error al cargar configuración de VeriFactu");
      setIsLoading(false);
    }
  };

  const fetchRecentInvoices = async () => {
    try {
      // Simulated - will be replaced with actual API call
      setRecentInvoices([]);
    } catch (error) {
      console.error("Error fetching recent invoices:", error);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setIsSaving(true);
      // await axios.put(`${API}/admin/verifactu/config`, config, { withCredentials: true });
      toast.success("Configuración guardada correctamente");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnableVeriFactu = async () => {
    if (!config.nif || !config.company_name) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }

    toast.info("Activando VeriFactu", {
      description: "Esta funcionalidad estará completamente operativa próximamente"
    });
  };

  const handleSyncAEAT = async () => {
    toast.info("Sincronización con AEAT", {
      description: "La conexión con la Agencia Tributaria se activará cuando se complete la certificación"
    });
  };

  const handleUploadCertificate = () => {
    toast.info("Subir certificado digital", {
      description: "La gestión de certificados digitales estará disponible próximamente"
    });
  };

  const handleDownloadBackup = () => {
    toast.info("Descargar respaldo", {
      description: "Se generará un archivo con todas las facturas firmadas"
    });
  };

  const getStatusBadge = () => {
    if (!config.enabled) {
      return <Badge variant="outline" className="text-yellow-500 border-yellow-500">
        <AlertTriangle className="w-3 h-3 mr-1" />Inactivo
      </Badge>;
    }

    if (config.certificate_valid) {
      return <Badge className="bg-green-500">
        <CheckCircle2 className="w-3 h-3 mr-1" />Activo y Certificado
      </Badge>;
    }

    return <Badge variant="destructive">
      <AlertTriangle className="w-3 h-3 mr-1" />Activo sin Certificado
    </Badge>;
  };

  return (
    <AdminLayout title="VeriFactu">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-500" />
              VeriFactu
            </h1>
            <p className="text-gray-400">
              Sistema de verificación de facturas - Agencia Tributaria Española
            </p>
          </div>
          {getStatusBadge()}
        </div>

        {/* Info Alert */}
        <Alert className="border-blue-500/20 bg-blue-500/10">
          <Info className="w-5 h-5 text-blue-500" />
          <AlertTitle className="text-blue-500">¿Qué es VeriFactu?</AlertTitle>
          <AlertDescription className="text-gray-300">
            VeriFactu es el sistema obligatorio de la AEAT para verificar facturas en sistemas de TPV.
            Garantiza la trazabilidad, integridad y autenticidad de todas las operaciones de venta mediante:
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Firma electrónica de facturas con certificado digital</li>
              <li>Hash encadenado de todas las transacciones</li>
              <li>Registro inalterable de operaciones</li>
              <li>Envío automático de información a la AEAT</li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Facturas</p>
                  <p className="text-2xl font-bold text-white">{config.total_invoices}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Pendientes Firma</p>
                  <p className="text-2xl font-bold text-white">{config.pending_signatures}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Hash className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Última Sincronización</p>
                  <p className="text-sm font-medium text-white">
                    {config.last_sync ? new Date(config.last_sync).toLocaleString('es-ES') : 'Nunca'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Certificado</p>
                  <p className="text-sm font-medium text-white">
                    {config.certificate_valid ? 'Válido' : 'No configurado'}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${config.certificate_valid ? 'bg-green-500/20' : 'bg-red-500/20'} flex items-center justify-center`}>
                  <Lock className={`w-6 h-6 ${config.certificate_valid ? 'text-green-500' : 'text-red-500'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Configuration */}
        <Card className="border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Settings className="w-5 h-5" />
              Configuración VeriFactu
            </CardTitle>
            <CardDescription>
              Configura los datos necesarios para cumplir con la normativa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="company" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-gray-900">
                <TabsTrigger value="company">Empresa</TabsTrigger>
                <TabsTrigger value="software">Software</TabsTrigger>
                <TabsTrigger value="certificate">Certificado</TabsTrigger>
                <TabsTrigger value="sync">Sincronización</TabsTrigger>
              </TabsList>

              {/* Company Tab */}
              <TabsContent value="company" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">NIF de la Empresa *</Label>
                    <Input
                      placeholder="B12345678"
                      value={config.nif}
                      onChange={(e) => setConfig({ ...config, nif: e.target.value })}
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-400 mt-1">NIF/CIF de tu empresa</p>
                  </div>
                  <div>
                    <Label className="text-white">Razón Social *</Label>
                    <Input
                      placeholder="Conexo POS S.L."
                      value={config.company_name}
                      onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-white">ID del Dispositivo</Label>
                    <Input
                      placeholder="POS-001-Conexo POS"
                      value={config.device_id}
                      onChange={(e) => setConfig({ ...config, device_id: e.target.value })}
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-400 mt-1">Identificador único de este TPV</p>
                  </div>
                </div>
              </TabsContent>

              {/* Software Tab */}
              <TabsContent value="software" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Nombre del Software</Label>
                    <Input
                      value={config.software_name}
                      disabled
                      className="mt-2 bg-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Versión</Label>
                    <Input
                      value={config.software_version}
                      disabled
                      className="mt-2 bg-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-white">NIF Fabricante</Label>
                    <Input
                      placeholder="B87654321"
                      value={config.manufacturer_nif}
                      onChange={(e) => setConfig({ ...config, manufacturer_nif: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Nombre Fabricante</Label>
                    <Input
                      placeholder="Local Flow"
                      value={config.manufacturer_name}
                      onChange={(e) => setConfig({ ...config, manufacturer_name: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                </div>
                <Alert className="border-gray-700">
                  <Info className="w-4 h-4" />
                  <AlertDescription className="text-sm text-gray-400">
                    Estos datos identifican el software ante la AEAT. El nombre y versión son automáticos.
                  </AlertDescription>
                </Alert>
              </TabsContent>

              {/* Certificate Tab */}
              <TabsContent value="certificate" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <Alert className={`${config.certificate_valid ? 'border-green-500/20 bg-green-500/10' : 'border-yellow-500/20 bg-yellow-500/10'}`}>
                    {config.certificate_valid ? (
                      <>
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        <AlertTitle className="text-green-500">Certificado Digital Válido</AlertTitle>
                        <AlertDescription className="text-gray-300">
                          Tu certificado digital está instalado y es válido hasta: {config.certificate_expiry || 'N/A'}
                        </AlertDescription>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                        <AlertTitle className="text-yellow-500">Certificado Digital No Configurado</AlertTitle>
                        <AlertDescription className="text-gray-300">
                          Necesitas un certificado digital de la FNMT o proveedor autorizado para firmar facturas.
                        </AlertDescription>
                      </>
                    )}
                  </Alert>

                  <div className="space-y-3">
                    <Button onClick={handleUploadCertificate} className="w-full" variant="outline">
                      <Upload className="w-4 h-4 mr-2" />
                      Subir Certificado Digital (.pfx / .p12)
                    </Button>
                    <div className="text-sm text-gray-400 space-y-1">
                      <p>• Soporta certificados de la FNMT, Camerfirma, y otros proveedores autorizados</p>
                      <p>• El certificado debe estar a nombre de la empresa (NIF empresarial)</p>
                      <p>• Se recomienda renovar el certificado 30 días antes de su expiración</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Sync Tab */}
              <TabsContent value="sync" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg">
                    <div>
                      <Label className="text-white">Sincronización Automática con AEAT</Label>
                      <p className="text-sm text-gray-400 mt-1">
                        Enviar facturas automáticamente cada hora
                      </p>
                    </div>
                    <Switch
                      checked={config.enabled}
                      onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={handleSyncAEAT} variant="outline">
                      <Send className="w-4 h-4 mr-2" />
                      Sincronizar Ahora
                    </Button>
                    <Button onClick={handleDownloadBackup} variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Respaldo
                    </Button>
                  </div>

                  <Alert className="border-gray-700">
                    <Calendar className="w-4 h-4" />
                    <AlertDescription className="text-sm text-gray-400">
                      La última sincronización exitosa fue: {config.last_sync ? new Date(config.last_sync).toLocaleString('es-ES') : 'Nunca'}
                    </AlertDescription>
                  </Alert>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSaveConfig} disabled={isSaving} className="flex-1">
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FileCheck className="w-4 h-4 mr-2" />
                    Guardar Configuración
                  </>
                )}
              </Button>
              {!config.enabled && (
                <Button onClick={handleEnableVeriFactu} variant="outline">
                  <Shield className="w-4 h-4 mr-2" />
                  Activar VeriFactu
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card className="border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Facturas Recientes</CardTitle>
            <CardDescription>
              Últimas facturas firmadas con VeriFactu
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentInvoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay facturas firmadas todavía</p>
                <p className="text-sm text-gray-500 mt-1">
                  Las facturas aparecerán aquí una vez que actives VeriFactu
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentInvoices.map((invoice, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm font-medium text-white">{invoice.number}</p>
                        <p className="text-xs text-gray-400">{invoice.date}</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Firmada
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Requirements Checklist */}
        <Card className="border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Requisitos de Cumplimiento</CardTitle>
            <CardDescription>
              Asegúrate de cumplir todos los requisitos obligatorios
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${config.nif ? 'bg-green-500' : 'bg-gray-700'}`}>
                  {config.nif ? <CheckCircle2 className="w-3 h-3 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">NIF de la empresa configurado</p>
                  <p className="text-xs text-gray-400">Identificación fiscal obligatoria</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${config.certificate_valid ? 'bg-green-500' : 'bg-gray-700'}`}>
                  {config.certificate_valid ? <CheckCircle2 className="w-3 h-3 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Certificado digital instalado</p>
                  <p className="text-xs text-gray-400">Necesario para firmar facturas</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${config.device_id ? 'bg-green-500' : 'bg-gray-700'}`}>
                  {config.device_id ? <CheckCircle2 className="w-3 h-3 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">ID del dispositivo asignado</p>
                  <p className="text-xs text-gray-400">Identificador único del TPV</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${config.enabled ? 'bg-green-500' : 'bg-gray-700'}`}>
                  {config.enabled ? <CheckCircle2 className="w-3 h-3 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Sistema activado y funcionando</p>
                  <p className="text-xs text-gray-400">VeriFactu debe estar activo para cumplir normativa</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
